import React, { useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Trash2, StickyNote, Loader2 } from "lucide-react";
import { toast } from "sonner";

const roleLabel = { super_admin: "Success Manager", admin: "Success Manager", student: "Student" };

export default function NotesPanel({ entityType, entityId, title = "Notes" }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isStaff = user?.role === "admin" || user?.role === "super_admin";

  const load = useCallback(async () => {
    try {
      const r = await client.get("/notes", { params: { entity_type: entityType, entity_id: entityId } });
      setNotes(r.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      const r = await client.post("/notes", { entity_type: entityType, entity_id: entityId, text: t });
      setNotes((prev) => [r.data, ...prev]);
      setText("");
    } catch (e) { toast.error("Couldn't save note", { description: e?.response?.data?.detail }); }
    finally { setSaving(false); }
  };

  const remove = async (n) => {
    try {
      await client.delete(`/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
      toast.success("Note deleted");
    } catch (e) { toast.error("Couldn't delete", { description: e?.response?.data?.detail }); }
  };

  const canDelete = (n) => isStaff || n.author_user_id === user?.user_id;
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (e) { return iso; } };

  return (
    <div data-testid="notes-panel">
      <div className="mb-3 flex items-center gap-2">
        <StickyNote className="h-4 w-4 text-brand-red" />
        <h3 className="font-display text-lg font-semibold text-white">{title}</h3>
        <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-muted-foreground">{notes.length}</span>
      </div>

      <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
        <Textarea data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Add a note… (timestamped and attributed to you)"
          className="min-h-[70px] resize-none border-white/10 bg-input/50 focus-visible:ring-brand-red" />
        <div className="mt-2 flex justify-end">
          <Button data-testid="add-note-btn" size="sm" onClick={add} disabled={saving || !text.trim()}
            className="bg-brand-red hover:bg-brand-redDark text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add note"}
          </Button>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {loading ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Loading notes…</p>
        ) : notes.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No notes yet.</p>
        ) : notes.map((n) => (
          <div key={n.id} className="rounded-xl border border-white/8 bg-white/[0.02] p-3" data-testid="note-item">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-white">{n.author_name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${n.author_role === "student" ? "bg-white/8 text-muted-foreground" : "bg-brand-green/15 text-brand-green"}`}>
                    {roleLabel[n.author_role] || "Member"}
                  </span>
                  <span className="text-[10px] text-muted-foreground/70">{fmt(n.created_at)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{n.text}</p>
              </div>
              {canDelete(n) && (
                <button data-testid="delete-note-btn" onClick={() => remove(n)}
                  className="shrink-0 text-muted-foreground transition-colors hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
